import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type { CrmActivity } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { Button, Card, Checkbox, Chip, Empty, Loading, PageHeader } from '../../components/ui';

export function CrmTasks() {
  const { company } = useAuth();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const mine = params.get('mine') === 'true';
  const tasks = useQuery({
    queryKey: ['crm-tasks', company?.id, mine],
    queryFn: () => capi<CrmActivity[]>(`/crm/activities?openTasks=true&limit=500${mine ? '&mine=true' : ''}`),
  });
  const done = useMutation({
    mutationFn: (id: string) => capi(`/crm/activities/${id}`, { method: 'PATCH', json: { done: true } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-tasks'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const now = Date.now();
  return (
    <>
      <PageHeader crumb={t('crmCrumb')} title={t('tasksTitle')} sub={t('tasksSub')}
        actions={<Checkbox label={t('filterMine')} checked={mine} onChange={(e) => setParams(e.target.checked ? { mine: 'true' } : {}, { replace: true })} />} />
      <Card pad={false} className="table">
        {!tasks.data ? <Loading error={tasks.error} /> : (
          <table>
            <thead><tr><th>{t('colTask')}</th><th>{t('colAccount')}</th><th>{t('fDueAt')}</th><th className="r" /></tr></thead>
            <tbody>
              {tasks.data.length === 0 && <Empty />}
              {tasks.data.map((a) => {
                const late = !!a.dueAt && Date.parse(a.dueAt) < now;
                return (
                  <tr key={a.id}>
                    <td><div style={{ fontWeight: 600 }}>{a.subject}</div><div className="sub">{[a.personName, a.createdByName ?? a.createdVia].filter(Boolean).join(' · ')}</div></td>
                    <td><Link to={`/crm/accounts/${a.accountId}`}>{a.accountName}</Link></td>
                    <td>{late ? <Chip tone="danger">{t('overdue')} · {formatDate(a.dueAt, lang)}</Chip> : <span className="small">{formatDate(a.dueAt, lang, true)}</span>}</td>
                    <td className="r"><Button size="sm" icon="fas fa-check" disabled={done.isPending} onClick={() => done.mutate(a.id)}>{t('markDone')}</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
